import { Big } from 'big.js';
import { Side } from '@/common/constants';
import { stringToBig } from './bigUtils';

/** Q16 fixed-point denominator for the entry-price residue (`Position.epr`, PNSQ16). */
const ENTRY_PRICE_RESIDUE_SCALE = 65536;

export interface EffectiveEntryPriceParams {
  side: Side;
  /** Stored entry price in human units (already divided by 10^priceDecimals). */
  entryPrice: number | string | Big;
  /** Q16 fractional residue of the stored entry price (`Position.epr`). Defaults to 0 (no lift). */
  entryPriceResidue?: number;
  /** Market price decimals — the exponent the stored price was scaled by on the wire. */
  priceDecimals: number;
}

/**
 * Reconstructs the canonical entry price the protocol uses for delta-PnL from the
 * asymmetrically-rounded stored price plus the sub-LSB rounding residue (`Position.epr`,
 * Q16 PNSQ16). Mirrors `CalculationsLib.computeDeltaPnlWithResidue`:
 *
 *   LONG  (residue > 0): (entryPrice − 1 LSB) + (residue / 65536) LSB
 *   LONG  (residue == 0): entryPrice
 *   SHORT:                entryPrice + (residue / 65536) LSB
 *
 * where 1 LSB = 10^(−priceDecimals) in human units (one unit of the stored PNS price). With
 * `entryPriceResidue` absent or 0 the result equals `entryPrice` exactly, preserving legacy
 * behavior for positions opened before the residue field shipped.
 *
 * This is for PnL math only; the displayed entry price keeps using the stored value.
 */
export function effectiveEntryPriceForPnl({
  side,
  entryPrice,
  entryPriceResidue = 0,
  priceDecimals,
}: EffectiveEntryPriceParams): Big {
  const entryPriceBig = stringToBig(entryPrice.toString());
  if (!entryPriceResidue) return entryPriceBig;

  const priceLsb = new Big(10).pow(-priceDecimals);
  const residueOffset = new Big(entryPriceResidue).div(ENTRY_PRICE_RESIDUE_SCALE).times(priceLsb);

  return side === Side.long ? entryPriceBig.minus(priceLsb).plus(residueOffset) : entryPriceBig.plus(residueOffset);
}

export interface UnrealizedPnlParams {
  side: Side;
  size: number | string | Big;
  entryPrice: number | string | Big;
  markPrice: number | string | Big;
  /** Funding sum recorded at position entry, after price-decimals conversion but still scaled by fundingSumDivider. */
  entryFundingSum: number | string | Big;
  /** Latest market funding sum, after price-decimals conversion but still scaled by fundingSumDivider. */
  currentFundingSum: number | string | Big;
  /** Literal divider that scaled both funding sums (`FundingEvent.div` = 10^exp, already exponentiated). Defaults to 1. */
  fundingSumDivider?: number;
}

/**
 * Unrealized PnL components for an open position.
 *
 * Sign convention (matches the protocol):
 *   pricePnl   = (mark - entry) * size * signed
 *   premiumPnl = (entryFundingSum - currentFundingSum) * size * signed / fundingSumDivider
 *   signed     = +1 for long, -1 for short
 */
export function calculateUnrealizedPnl({
  side,
  size,
  entryPrice,
  markPrice,
  entryFundingSum,
  currentFundingSum,
  fundingSumDivider,
}: UnrealizedPnlParams): { pricePnl: Big; premiumPnl: Big } {
  const sizeBig = stringToBig(size.toString());
  const signed = side === Side.long ? 1 : -1;

  const pricePnl = stringToBig(markPrice.toString()).minus(stringToBig(entryPrice.toString())).times(signed).times(sizeBig);

  // `|| 1` also guards zero, which Big.div would throw on
  const scaler = new Big(fundingSumDivider || 1);
  const premiumPnl = stringToBig(entryFundingSum.toString())
    .minus(stringToBig(currentFundingSum.toString()))
    .times(sizeBig)
    .times(signed)
    .div(scaler);

  return { pricePnl, premiumPnl };
}
